'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { NotificationsSheet } from '@/partials/topbar/notifications-sheet';
import { UserDropdownMenu } from '@/partials/topbar/user-dropdown-menu';
import CartBadge from '@/partials/topbar/cart-badge';
import {
  Bell,
  Brain,
  Menu,
  Search,
  Sparkles,
  SquareChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useScrollPosition } from '@/hooks/use-scroll-position';
import { BackendAuth } from '@/lib/auth-backend';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Container } from '@/components/common/container';
import { Breadcrumb } from './breadcrumb';
import { MegaMenu } from './mega-menu';
import { MegaMenuMobile } from './mega-menu-mobile';
import { SidebarMenu } from './sidebar-menu';
import FocusStatusBadge from '@/components/focus/focus-status-badge';
import AiUsageWidget from '@/components/ai-usage-widget';
import { useDomainLogos } from '@/hooks/use-domain-config';
import AdminJobStatusIndicator from '@/components/admin/job-status-indicator';
import { BugReportButton } from '@/app/components/bug-report/bug-report-sheet';
import { usePersonalAI } from '@/providers/personal-ai-provider';
import {
  HeaderActionsOverflow,
  HeaderActionTile,
} from '@/components/layouts/header-actions-overflow';
import { useTranslation } from '@/hooks/useTranslation';

export function Header() {
  const [isSidebarSheetOpen, setIsSidebarSheetOpen] = useState(false);
  const [isMegaMenuSheetOpen, setIsMegaMenuSheetOpen] = useState(false);

  const pathname = usePathname();
  const router = useRouter();
  const mobileMode = useIsMobile();
  const user = BackendAuth.getUser();
  const logos = useDomainLogos();
  const isAdminUser = useMemo(() => {
    if (!user) return false;
    const raw = user.is_admin ?? user.isAdmin ?? user.role;
    if (raw === true) return true;
    if (typeof raw === 'number') return raw === 1;
    if (typeof raw === 'string') {
      const s = raw.toLowerCase();
      if (s === '1' || s === 'true' || s.includes('admin')) return true;
    }
    const role = (user?.role || '').toLowerCase();
    return role === 'admin' || role === 'super_admin';
  }, [user]);

  const scrollPosition = useScrollPosition();
  const headerSticky = scrollPosition > 0;

  // End-user Personal-AI assistant: toggle kommt vom PersonalAIProvider
  // (mounted in (protected)/layout.jsx). Der fruehere access-check-Roundtrip
  // ist OBSOLET entfernt — hasAccess ist immer true (Rechte via Rechtegruppen).
  const { hasAccess: personalAiAccess, togglePanel: togglePersonalAi } = usePersonalAI();
  const { t } = useTranslation();

  // Core-Agent-Button: Admin ODER erzwungen via ?ai=1 / localStorage.debug-ai=1.
  // Bewusst im Effect statt inline im Render (SSR-sicher, kein Hydration-Mismatch).
  const [forceAgentVisible, setForceAgentVisible] = useState(false);
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      setForceAgentVisible(
        sp.get('ai') === '1' || localStorage.getItem('debug-ai') === '1',
      );
    } catch {
      /* ignore */
    }
  }, []);
  const showCoreAgent = isAdminUser || forceAgentVisible;

  const toggleCoreAgent = () => {
    try {
      window.dispatchEvent(new CustomEvent('xoffi-agent:toggle'));
    } catch {
      /* ignore */
    }
  };

  // Einzelne Icon-Controls einmal definieren — sie werden je nach Viewport
  // entweder inline (Desktop) oder als Kachel im ⋯-Sheet (mobil) gerendert.
  const contactsButton = (
    <Button
      data-tour="header-search"
      variant="ghost"
      mode="icon"
      shape="circle"
      className="size-9 hover:bg-primary/10 hover:[&_svg]:text-primary"
      onClick={() => router.push('/contacts')}
      title={t('header.actions.contacts', { defaultValue: 'Kontakte', editable: false })}
    >
      <Search className="size-4.5!" />
    </Button>
  );

  const assistantButton =
    personalAiAccess === true ? (
      <Button
        data-tour="header-assistant"
        variant="ghost"
        mode="icon"
        shape="circle"
        className="size-9 hover:bg-primary/10 hover:[&_svg]:text-primary"
        onClick={() => togglePersonalAi()}
        title={t('header.actions.assistant', { defaultValue: 'Assistent', editable: false })}
        aria-label={t('header.actions.assistant', { defaultValue: 'Assistent', editable: false })}
      >
        <Sparkles className="size-4.5!" />
      </Button>
    ) : null;

  const coreAgentButton = showCoreAgent ? (
    <Button
      variant="ghost"
      mode="icon"
      shape="circle"
      className="size-9 hover:bg-primary/10 hover:[&_svg]:text-primary"
      onClick={toggleCoreAgent}
      title="XOFFI Agent - Opus 4.1 Orchestrierung"
    >
      <Brain className="size-4.5!" />
    </Button>
  ) : null;

  // Close sheet when route changes
  useEffect(() => {
    setIsSidebarSheetOpen(false);
    setIsMegaMenuSheetOpen(false);
  }, [pathname]);

  return (
    <header
      className={cn(
        'header fixed top-0 z-10 start-0 flex items-stretch shrink-0 border-b border-transparent bg-background end-0 pe-[var(--removed-body-scroll-bar-size,0px)]',
        headerSticky && 'border-b border-border',
      )}
    >
      <Container className="flex justify-between items-stretch lg:gap-4">
        {/* HeaderLogo */}
        <div className="flex gap-1 lg:hidden items-center gap-2.5">
          <Link href="/" className="shrink-0">
            <img
              src={logos.small}
              className="h-[25px] w-full"
              alt="mini-logo"
            />
          </Link>
          <div className="flex items-center">
            {mobileMode && (
              <Sheet
                open={isSidebarSheetOpen}
                onOpenChange={setIsSidebarSheetOpen}
              >
                <SheetTrigger asChild>
                  <Button variant="ghost" mode="icon">
                    <Menu className="text-muted-foreground/70" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  className="p-0 gap-0 w-[275px]"
                  side="left"
                  close={false}
                >
                  <SheetHeader className="p-0 space-y-0" />
                  <SheetBody className="p-0 overflow-y-auto">
                    <SidebarMenu />
                  </SheetBody>
                </SheetContent>
              </Sheet>
            )}
            {mobileMode && (
              <Sheet
                open={isMegaMenuSheetOpen}
                onOpenChange={setIsMegaMenuSheetOpen}
              >
                <SheetTrigger asChild>
                  <Button variant="ghost" mode="icon">
                    <SquareChevronRight className="text-muted-foreground/70" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  className="p-0 gap-0 w-[275px]"
                  side="left"
                  close={false}
                >
                  <SheetHeader className="p-0 space-y-0" />
                  <SheetBody className="p-0 overflow-y-auto">
                    <MegaMenuMobile />
                  </SheetBody>
                </SheetContent>
              </Sheet> 
            )}
          </div>
        </div>

        {/* Main Content (MegaMenu or Breadcrumbs) */}
        <div className="flex-1 flex items-center">
          {pathname.startsWith('/account/') ? (
            <Breadcrumb />
          ) : (
            !mobileMode && <MegaMenu />
          )}
        </div>

        {/* HeaderTopbar — shrink-0: darf NIE zusammengedrückt oder aus dem
            Viewport geschoben werden (sonst ist das Profil-Menü unerreichbar) */}
        <div className="flex items-center gap-1.5 lg:gap-3 shrink-0">
          {(
            <>
              {!mobileMode && contactsButton}
              <NotificationsSheet
                trigger={
                  <Button
                    variant="ghost"
                    mode="icon"
                    shape="circle"
                    className="size-9 hover:bg-primary/10 hover:[&_svg]:text-primary"
                  >
                    <Bell className="size-4.5!" />
                  </Button>
                }
              />

              {/* Sekundäre Aktionen.
                  Desktop (>= lg): alle inline wie bisher.
                  Mobil (< lg): gebündelt im ⋯-Bottom-Sheet — sonst schiebt die
                  Icon-Reihe den Avatar (Profil/Abmelden) aus dem Viewport.
                  Bewusst per mobileMode statt CSS-Toggle: so wird jedes Widget
                  nur EINMAL gemountet (kein doppeltes Polling). */}
              {mobileMode ? (
                <HeaderActionsOverflow
                  title={t('header.actions.more', { defaultValue: 'Weitere Aktionen', editable: false })}
                >
                  <HeaderActionTile
                    label={t('header.actions.contacts', { defaultValue: 'Kontakte', editable: false })}
                  >
                    {contactsButton}
                  </HeaderActionTile>

                  <HeaderActionTile
                    label={t('header.actions.cart', { defaultValue: 'Warenkorb', editable: false })}
                  >
                    <CartBadge />
                  </HeaderActionTile>

                  <HeaderActionTile
                    label={t('header.actions.focus', { defaultValue: 'Fokus-Status', editable: false })}
                  >
                    <FocusStatusBadge />
                  </HeaderActionTile>

                  <HeaderActionTile
                    label={t('header.actions.jobs', { defaultValue: 'Jobs', editable: false })}
                  >
                    <AdminJobStatusIndicator isAdmin={isAdminUser} />
                  </HeaderActionTile>

                  <HeaderActionTile
                    label={t('header.actions.bugReport', { defaultValue: 'Fehler melden', editable: false })}
                  >
                    <BugReportButton />
                  </HeaderActionTile>

                  <HeaderActionTile
                    label={t('header.actions.assistant', { defaultValue: 'Assistent', editable: false })}
                  >
                    {assistantButton}
                  </HeaderActionTile>

                  <HeaderActionTile
                    label={t('header.actions.agent', { defaultValue: 'XOFFI Agent', editable: false })}
                  >
                    {coreAgentButton}
                  </HeaderActionTile>

                  <HeaderActionTile
                    label={t('header.actions.aiUsage', { defaultValue: 'KI-Verbrauch', editable: false })}
                  >
                    <AiUsageWidget />
                  </HeaderActionTile>
                </HeaderActionsOverflow>
              ) : (
                <>
                  {/* Warenkorb */}
                  <CartBadge />

                  {/* Global Focus Status */}
                  <FocusStatusBadge />

                  <AdminJobStatusIndicator isAdmin={isAdminUser} />

                  {/* Bug Report */}
                  <BugReportButton />

                  {/* Admin tools (BG-animation, Chat, Nexus Background Agent) +
                      Super-Admin theme preview now live INSIDE the profile/avatar
                      dropdown (AdminProfileSections in UserDropdownMenu). The
                      standalone Shield menu was removed. */}

                  {/* End-user Personal-AI assistant toggle (Sparkles). Shown for
                      every user with AI access (nexus_ai_access, default "all"). */}
                  {assistantButton}

                  {/* Core Agent Toggle (Admin or forced via ?ai=1 / debug-ai=1) */}
                  {coreAgentButton}

                  {/* AI-Verbrauch Mini-Widget */}
                  <AiUsageWidget />
                </>
              )}

              <UserDropdownMenu
                trigger={
                  <img
                    data-tour="user-profile"
                    className="size-9 rounded-full border-2 border-green-500 shrink-0 cursor-pointer"
                    src={BackendAuth.getUserAvatarUrl()}
                    alt="User Avatar"
                  />
                }
              />
            </>
          )}
        </div>
      </Container>
    </header>
  );
}
